import gql from "graphql-tag";

import {loginWithPassword} from "@khan/auth-password";
import {calculateNextUrl, type LoginTypeData} from "@khan/auth-shared";
import {type GqlFetchFn} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {Errors, KAError} from "@khan/logging";
import {redirect, type Location} from "@khan/urls";

import type {linkCleverAccountV2MutationType} from "./__graphql-types__/index.ts";

export const loginCleverUser = (
    gqlFetch: GqlFetchFn,
    identifier: string,
    password: string,
): Promise<LoginTypeData> => {
    return loginWithPassword(gqlFetch, {identifier, password});
};

export const completePostLogin = async (
    gqlFetch: GqlFetchFn,
    isTeacher: boolean,
    continueUrl: string,
    loginData?: LoginTypeData | null,
    location?: Location,
) => {
    // The request is coming from the loginWithPassword mutation.
    if (loginData && location) {
        const nextUrl = await calculateNextUrl({
            gqlFetch,
            location: location,
            loginType: "clever_library",
            linkClever: true,
            continueUrl: continueUrl || "/",
            isFirstLogin: loginData.isFirstLogin,
            joinClassrooms: loginData.joinClassrooms,
        });
        redirect(nextUrl);
    }
};

export const logoutCleverUser = (location: Location, step: string) => {
    const {pathname, origin} = location;
    const continueUrl = origin + pathname + "?step=" + step;
    redirect("/logout?allowClever=1&continue=" + continueUrl);
};

export const CLEVER_LINK_V2_MUTATION: GqlOperation<
    linkCleverAccountV2MutationType["response"],
    linkCleverAccountV2MutationType["variables"]
> = gqlOp(gql`
    mutation linkCleverAccountV2Mutation($transferToken: String!) {
        linkCleverAccountV2(transferToken: $transferToken) {
            error {
                code
            }
        }
    }
`);

export const linkClever = async (
    gqlFetch: GqlFetchFn,
    options: linkCleverAccountV2MutationType["variables"],
) => {
    try {
        const result = await gqlFetch(CLEVER_LINK_V2_MUTATION, {
            variables: options,
        });

        const data = result.linkCleverAccountV2;
        if (data?.error) {
            throw new KAError(data.error.code, Errors.Internal);
        }
    } catch (error) {
        if (error instanceof KAError) {
            throw error;
        }

        // Throw a generic error.
        throw new KAError("Failed to link account ", Errors.Internal);
    }
};
